/**
 * ListToolbar — the product's ONE toolbar. Every list, board and report row of
 * controls is this component, in this order, with these words:
 *
 *   [scope] [search] [Filter] [Sort] [Group] [Columns] [layout] [density] … [count] [secondary] [primary]
 *
 * Every control is optional; the ORDER and the VOCABULARY are not. A screen with
 * no grouping omits `group` and the row is otherwise identical — same 40px
 * height, same 12px gutters, same search field, same words. That constancy is
 * the whole point: five toolbar vocabularies in nine screens is the thing a
 * stranger notices in ten seconds without being able to name it.
 *
 * The toolbar owns the *trigger*, never the panel behind it. A screen keeps its
 * own FilterBuilder / ColumnPicker / sort menu and wraps the canonical trigger:
 *
 *   <ListToolbar
 *     search={{ value: q, onChange: setQ, noun: 'inquiries' }}
 *     filter={{ active: conditions.length, render: (t) => <FilterBuilder trigger={t} … /> }}
 *     sort={{ summary: 'Newest first', render: (t) => <Menu trigger={t} items={sortItems} /> }}
 *     columns={{ hidden: 3, render: (t) => <ColumnPicker trigger={t} … /> }}
 *     density={{ value: density, onChange: setDensity }}
 *     count={{ matching: total, total: corpus, noun: 'inquiry' }}
 *     action={<CreateMenu onCreate={create} />}
 *   />
 *
 * Rules the props enforce rather than document:
 *  - the search placeholder is built from a noun, so no screen can invent a third
 *    placeholder voice, and no number can leak into it (numbers are the count's job);
 *  - the count is a `CountLabel`, so "of 25 loaded" is unsayable;
 *  - the primary action sits last, on the right, in every theme and on every screen.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  ArrowUpDown,
  Columns3,
  Group as GroupIcon,
  LayoutGrid,
  ListFilter,
  MoreHorizontal,
  Rows2,
  Rows3,
  Search,
  Table2,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import type { Density } from '@/lib/constants';
import { registerScopedSearch } from '@/lib/scopedSearch';
import { Button } from './Button';
import { CountLabel, type CountLabelProps } from './CountLabel';
import { IconButton } from './IconButton';
import { Input } from './Input';
import { Kbd } from './Kbd';
import { Menu, type MenuItem } from './Menu';
import { SegmentedControl } from './SegmentedControl';

/* ────────────────────────────────────────────────────────────────────────────
   Slot shapes
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * A control's panel belongs to the screen; the trigger belongs to the toolbar.
 * `render` receives the canonical trigger element and returns it wrapped in
 * whatever popover the screen owns. Use `onClick` instead for a control that
 * opens nothing (a straight toggle).
 */
export type ToolbarTriggerRender = (trigger: ReactElement) => ReactNode;

interface ToolbarControlBase {
  render?: ToolbarTriggerRender;
  onClick?: () => void;
  disabled?: boolean;
}

export interface ListToolbarSearch {
  value: string;
  onChange: (next: string) => void;
  /** Plural noun — `inquiries`, `contacts`. Becomes `Search inquiries…`. */
  noun: string;
  /** So the shell's `/` shortcut can put the caret here. */
  inputRef?: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}

export interface ListToolbarFilter extends ToolbarControlBase {
  /** Number of active conditions. `0`/omitted renders the quiet resting state. */
  active?: number;
  /**
   * The conditions themselves, in the reader's words: `Europe, 5+ inquiries`.
   * The same slot, rendered the same way, as Sort's — two controls in one row
   * must not disclose their state two different ways, and a bare count tells a
   * reader looking at an empty list to loosen a condition it will not name.
   * Omit it and the control falls back to the badge alone.
   */
  summary?: string;
}

export interface ListToolbarSort extends ToolbarControlBase {
  /** The current order in the user's words: `Newest first`, `Score, high to low`. */
  summary?: string;
}

export interface ListToolbarGroup extends ToolbarControlBase {
  /** The current grouping: `Stage`, `Owner`, `None`. */
  summary?: string;
}

export interface ListToolbarColumns extends ToolbarControlBase {
  /**
   * How many columns are hidden — shown as the same badge Filter uses, but on a
   * ghost trigger rather than a filled one.
   *
   * Pass it only when the visible set has actually been *changed* from the
   * screen's default. A 25-column entity with an 8-column default is "17
   * hidden" before the user has touched anything, and a resting toolbar whose
   * loudest control is its least important one, wearing a number that counts
   * down as the viewport widens, is worse than no number at all.
   */
  hidden?: number;
}

export interface ListToolbarDensity {
  value: Density;
  onChange: (next: Density) => void;
  disabled?: boolean;
}

/**
 * How the rows are DRAWN — a table of columns, or one card per record.
 *
 * A sibling of `density` rather than a mode of it: density trades vertical air
 * for rows within one shape, this changes the shape. It sits in the same slot
 * because both answer "how much of each row do I want to see", and a surface
 * that offers both puts them side by side.
 *
 * Only the inbox passes it, and only on a phone — see `InquiryCardList`.
 */
export type ListLayout = 'table' | 'cards';

export interface ListToolbarLayout {
  value: ListLayout;
  onChange: (next: ListLayout) => void;
  disabled?: boolean;
}

export interface ListToolbarProps {
  /**
   * The SCOPE slot — the only thing that may precede search, and only ever a
   * control that names the whole dataset the rest of the row then narrows: the
   * saved view (`ViewMenu`) on a list or a board. It exists so a surface whose
   * app-bar crumb already prints its name has somewhere to put "which list am
   * I in" without spending a second 52px `PageHeader` band on printing that
   * name a third time. One control, first, before search. Nothing else.
   */
  view?: ReactNode;
  /**
   * The other slot that may precede search: a time scope (`DateRangePicker`) on
   * a surface whose whole dataset is a range, i.e. Analytics. Nothing else.
   */
  range?: ReactNode;
  search?: ListToolbarSearch;
  filter?: ListToolbarFilter;
  sort?: ListToolbarSort;
  group?: ListToolbarGroup;
  columns?: ListToolbarColumns;
  density?: ListToolbarDensity;
  layout?: ListToolbarLayout;

  /** Right cluster, in order: the count… */
  count?: CountLabelProps;
  /** …then at most one quiet control (Export, Refresh)… */
  secondary?: ReactNode;
  /** …then the primary action — gold, last, every screen. */
  action?: ReactNode;
  /** …with anything rarer folded into one overflow menu. */
  overflow?: MenuItem[];

  /** Accessible name for the control group. Defaults to `List controls`. */
  label?: string;
  className?: string;
}

/* ────────────────────────────────────────────────────────────────────────────
   Shared bits
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * The one badge shape a toolbar control uses to say "N of these are on".
 *
 * `tone` is the exception that keeps that sentence true. Columns is the only
 * control whose number counts what is *off*, and in the gold it was the same
 * pill as Filter's saying the opposite thing 200px away — "Filter 1" (one
 * condition applied) beside "Columns 2" (two columns removed). An exclusion is
 * not an accent, so it wears the neutral chip instead.
 */
function ControlBadge({
  value,
  text,
  tone = 'accent',
}: {
  value: number;
  text?: string;
  tone?: 'accent' | 'neutral';
}) {
  return (
    <span
      className={cn(
        'grid h-4 min-w-4 place-items-center rounded-[var(--radius-full)] px-1 text-xs font-semibold tnum',
        tone === 'accent'
          ? 'bg-[var(--accent-soft)] text-accent-text'
          : 'bg-[var(--surface-3)] text-muted',
      )}
    >
      {text ?? value}
    </span>
  );
}

/** Every popover control in the row is this button — same size, same weight. */
const ToolbarButton = forwardRef<
  HTMLButtonElement,
  {
    icon: ReactNode;
    label: string;
    summary?: string;
    badge?: number;
    /**
     * Stay `ghost` even when badged. Filter's badge counts conditions the user
     * turned *on*, so promoting it to `subtle` is the control telling you it is
     * doing something. Columns' badge counts what is *off*, which is not a state
     * worth making the loudest thing in the row.
     */
    quiet?: boolean;
    /** Says what the badge's digit means, for the channel that cannot see it. */
    announce?: string;
    /**
     * What the badge *shows*, when the digit alone would be read as the wrong
     * kind of number. Columns counts an exclusion, so it renders `−2`; a bare
     * `2` sitting 350px from "183 companies" reads as "2 columns".
     */
    badgeText?: string;
    /** `neutral` for a badge that counts an exclusion rather than a choice. */
    badgeTone?: 'accent' | 'neutral';
    disabled?: boolean;
    onClick?: () => void;
  }
>(function ToolbarButton(
  {
    icon,
    label,
    summary,
    badge,
    quiet = false,
    announce,
    badgeText,
    badgeTone,
    disabled,
    onClick,
    ...rest
  },
  ref,
) {
  const badged = Boolean(badge && badge > 0);
  return (
    <Button
      ref={ref}
      variant={badged && !quiet ? 'subtle' : 'ghost'}
      className="shrink-0"
      disabled={disabled}
      onClick={onClick}
      iconLeft={icon}
      iconRight={
        badged ? (
          <ControlBadge value={badge as number} text={badgeText} tone={badgeTone} />
        ) : undefined
      }
      aria-label={announce ?? (summary ? `${label}: ${summary}` : label)}
      {...rest}
    >
      {label}
      {summary ? (
        /*
         * `inline-block`, and that word is the whole cap.
         *
         * `Button` wraps its children in one `min-w-0 truncate` flex item, so
         * this span is a plain INLINE box inside it — and `max-width` and
         * `overflow` do not apply to a non-replaced inline box. The cap that has
         * been written here all along therefore measured nothing: with three
         * filter conditions applied the span rendered 381px wide, the Filter
         * button blew out to 471px, the toolbar overflowed its own `main` by
         * 89px with no scrollbar, and the screen's gold primary action was
         * pushed past the right edge of a 1440 viewport. As a block-level box
         * the cap engages and the button's width stops moving.
         *
         * 200px, not the 120 that was written here while it was inert. A cap is
         * a stop, not an editor: every clause this slot is *meant* to carry has
         * to render whole under it, or the cap starts cutting the grammar's own
         * examples — "Score, high to low" is 136px and `Europe, 5+ inquiries`
         * 126px, both of which 120 would have cut the moment it engaged. 200
         * holds every sort, group and two-condition filter summary in the
         * product and still pins the widest control in the row at ~284px,
         * against the ~382px at which this toolbar starts pushing its primary
         * action off a 1440 screen. A screen with more to say than that says how
         * many, not which — see `contactFilterSummary`.
         *
         * `align-bottom` because an inline-block with `overflow: hidden` sits on
         * the baseline by its bottom margin edge, which lifts the clause a
         * couple of pixels off the label beside it.
         */
        <span
          aria-hidden="true"
          className="inline-block max-w-[200px] truncate align-bottom text-muted"
        >
          {/* One neutral ink, not two. `--text-faint` was meant to put the
              separator a step under the clause it separates and does the
              opposite: against `--surface-1` it measures 5.80:1 in dark to
              `--text-muted`'s 5.35:1 — the "faint" token is the STRONGER of the
              two — and 5.63:1 to 5.66:1 in light, half a percent. So the dot
              inherits the clause's ink and the row stops asserting a rank the
              colours do not carry. */}
          <span className="px-1">·</span>
          {summary}
        </span>
      ) : null}
    </Button>
  );
});

/** Wrap the trigger in the screen's panel, or leave it standing on its own. */
function slot(trigger: ReactElement, render?: ToolbarTriggerRender): ReactNode {
  return render ? render(trigger) : trigger;
}

/* ────────────────────────────────────────────────────────────────────────────
   Search
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Long enough that a router navigation cannot land between two keystrokes at
 * 60wpm (~200ms/char), short enough that the list still feels like it is
 * following the caret.
 */
const SEARCH_DEBOUNCE_MS = 180;

/**
 * The field holds its own string. It has to.
 *
 * Every screen wires `onChange` to a query-string write, and
 * `setSearchParams(fn, {replace:true})` is not synchronous: React re-rendered a
 * *controlled* input back to the stale params-derived value in between
 * keystrokes, so the field both dropped and duplicated characters and wrote the
 * wreckage to the URL. Measured before this change, typing `concrete`:
 * 50ms/char → `e`, 80 → `ce`, 120 → `crte`, 200 → `ccete`.
 *
 * So: local state is the truth while the user types, `onChange` fires on a
 * trailing debounce, and the incoming prop only reseeds the field when it
 * differs from the last value this component emitted — i.e. when something
 * *other* than typing (Clear filters, a pasted link, the ⌘K palette) changed it.
 *
 * "DIFFERS FROM THE LAST VALUE THIS COMPONENT EMITTED" WAS NOT ENOUGH, and
 * `/tasks` proved it: 15 of 483 assertions and the whole `verify` pipeline died
 * at `search-typing` on "field read 'con' after typing 'conc'". The prop does
 * not arrive when it is emitted — it arrives when the screen it feeds finishes
 * rendering — so a heavy screen echoes value N back while the ref has already
 * moved to N+1, and the guard reads that stale echo as an EXTERNAL change and
 * clobbers the field mid-word. Probed live on /tasks at 200ms/char, typing
 * `concrete`: the field went "co" → (echo of "c") → "c" → "cn" → "co" → "coc",
 * and `?q=` settled on `cocrete`.
 *
 * So the guard is a round trip, not an equality: `pending` is raised on every
 * keystroke and lowered only when the value this component last emitted comes
 * back, or when `SEARCH_ECHO_MS` has passed with no echo at all (a screen that
 * clamps or ignores the query must not deafen the field forever). While it is
 * raised, an incoming value that is not the latest emitted one is an echo in
 * flight and is dropped. Nothing else about the contract moves: an external
 * change still reseeds, immediately, as soon as the typing burst has settled.
 */
const SEARCH_ECHO_MS = 700;

function ToolbarSearch({ search }: { search: ListToolbarSearch }) {
  const { value, onChange, noun, inputRef, disabled } = search;
  const [local, setLocal] = useState(value);
  const emitted = useRef(value);
  const timer = useRef<number | null>(null);
  /** An emit is in flight: the screen has not yet echoed it back. */
  const pending = useRef(false);
  const echoTimer = useRef<number | null>(null);

  /**
   * The field claims `/` while it is mounted.
   *
   * The chip and `aria-keyshortcuts` below are part of the toolbar grammar and
   * are not optional — which is exactly why the key has to reach this input:
   * every list surface was printing a boxed `/` on a field that `/` did not
   * focus, because the shell answered the shortcut with the command palette.
   * Registering here rather than in each screen means the promise and the
   * behaviour are the same object on all six surfaces. `inputRef` still gets the
   * node for any screen that wants it.
   */
  const ownRef = useRef<HTMLInputElement | null>(null);
  const setNode = useCallback(
    (node: HTMLInputElement | null) => {
      ownRef.current = node;
      if (inputRef) inputRef.current = node;
    },
    [inputRef],
  );
  useEffect(() => registerScopedSearch(ownRef), []);

  useEffect(() => {
    // The round trip landed: this is our own value coming home, and the field
    // is free to follow the prop again.
    if (value === emitted.current) {
      pending.current = false;
      if (echoTimer.current !== null) {
        window.clearTimeout(echoTimer.current);
        echoTimer.current = null;
      }
      return;
    }
    // An emit is still in flight, so a value that is not it is a stale echo of
    // an earlier one. Dropping it is the whole fix — reseeding here is what put
    // "cn" in the field after "con".
    if (pending.current) return;
    emitted.current = value;
    setLocal(value);
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      if (echoTimer.current !== null) window.clearTimeout(echoTimer.current);
    },
    [],
  );

  const push = useCallback(
    (next: string, immediate: boolean) => {
      setLocal(next);
      pending.current = true;
      if (timer.current !== null) window.clearTimeout(timer.current);
      // A screen that never echoes the query back (one that clamps it, or drops
      // it) must not leave the field deaf to external changes for the rest of
      // the session, so the guard also lapses on its own.
      if (echoTimer.current !== null) window.clearTimeout(echoTimer.current);
      echoTimer.current = window.setTimeout(() => {
        echoTimer.current = null;
        pending.current = false;
      }, SEARCH_DEBOUNCE_MS + SEARCH_ECHO_MS);
      const emit = () => {
        timer.current = null;
        emitted.current = next;
        onChange(next);
      };
      if (immediate) emit();
      else timer.current = window.setTimeout(emit, SEARCH_DEBOUNCE_MS);
    },
    [onChange],
  );

  return (
    <Input
      ref={setNode}
      value={local}
      disabled={disabled}
      onChange={(event) => push(event.target.value, false)}
      /* Clearing is a decision, not a keystroke — it lands at once. */
      onClear={() => push('', true)}
      clearable
      placeholder={`Search ${noun}…`}
      aria-label={`Search ${noun}`}
      aria-keyshortcuts="/"
      iconLeft={<Search aria-hidden />}
      iconRight={local.length === 0 ? <Kbd keys={['/']} size="sm" className="opacity-80" /> : undefined}
      containerClassName="w-[236px] min-w-[152px] shrink"
    />
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Toolbar
   ──────────────────────────────────────────────────────────────────────────── */

export function ListToolbar({
  view,
  range,
  search,
  filter,
  sort,
  group,
  columns,
  density,
  layout,
  count,
  secondary,
  action,
  overflow,
  label = 'List controls',
  className,
}: ListToolbarProps) {
  const hasRight = Boolean(count || secondary || action || (overflow && overflow.length > 0));

  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'flex h-10 shrink-0 items-center gap-1.5 border-b border-line-subtle px-3',
        /*
          One row is a desktop assumption.

          Every child of this row is `shrink-0`, and the row itself sits inside
          a shell that clips: measured at 390px, /tasks put `Export` at x=1120
          and `New task` at x=1225 — the surface's only create, 835px past the
          right edge of the screen, with no scrollbar to reach it. Nothing was
          hidden; it was simply gone.

          So below the breakpoint the row wraps into as many lines as it needs.
          Wrapping rather than collapsing into an overflow menu because these
          are the surface's controls in the order they were designed in — a
          reader looking for `Filter` finds it where the label says it is,
          one line lower.
        */
        'mobile:h-auto mobile:min-h-10 mobile:flex-wrap mobile:gap-y-1.5 mobile:py-1.5',
        className,
      )}
    >
      {view ? (
        <div className="flex min-w-0 shrink items-center gap-1.5">
          {view}
          {/* A hairline, not a gap: the scope names the dataset and everything
              to its right narrows it, which is a different kind of control. */}
          <span aria-hidden="true" className="mx-0.5 h-4 w-px shrink-0 bg-[var(--line)]" />
        </div>
      ) : null}

      {range ? <div className="flex shrink-0 items-center gap-1.5">{range}</div> : null}

      {search ? <ToolbarSearch search={search} /> : null}

      {filter
        ? slot(
            <ToolbarButton
              icon={<ListFilter aria-hidden />}
              label="Filter"
              summary={filter.summary}
              badge={filter.active}
              announce={
                filter.summary
                  ? `Filter — ${filter.active ?? 0} applied: ${filter.summary}`
                  : undefined
              }
              disabled={filter.disabled}
              onClick={filter.onClick}
            />,
            filter.render,
          )
        : null}

      {sort
        ? slot(
            <ToolbarButton
              icon={<ArrowUpDown aria-hidden />}
              label="Sort"
              summary={sort.summary}
              disabled={sort.disabled}
              onClick={sort.onClick}
            />,
            sort.render,
          )
        : null}

      {group
        ? slot(
            <ToolbarButton
              icon={<GroupIcon aria-hidden />}
              label="Group"
              summary={group.summary}
              disabled={group.disabled}
              onClick={group.onClick}
            />,
            group.render,
          )
        : null}

      {columns
        ? slot(
            <ToolbarButton
              icon={<Columns3 aria-hidden />}
              label="Columns"
              badge={columns.hidden}
              // A minus, because this is the one badge in the row that counts
              // what is *missing*. "2" beside a toolbar whose other number is
              // "183 companies" reads as a population; "−2" cannot.
              badgeText={columns.hidden ? `−${columns.hidden}` : undefined}
              // …and the neutral chip, not the gold one. The gold pill is the
              // product's "N of these are on"; this number is "N of these are
              // off", and the two must not be the same object.
              badgeTone="neutral"
              quiet
              announce={
                columns.hidden && columns.hidden > 0
                  ? `Columns — ${columns.hidden} hidden`
                  : 'Columns'
              }
              disabled={columns.disabled}
              onClick={columns.onClick}
            />,
            columns.render,
          )
        : null}

      {layout ? (
        <SegmentedControl
          size="md"
          className="shrink-0"
          aria-label="Row layout"
          value={layout.value}
          disabled={layout.disabled}
          onValueChange={(next) => layout.onChange(next === 'cards' ? 'cards' : 'table')}
          options={[
            /* Not `Rows2`/`Rows3`: those two are the density control 8px to the
               right, and a layout switch drawn in the density switch's own
               glyphs is two controls saying one thing. */
            { value: 'cards', icon: <LayoutGrid aria-hidden />, 'aria-label': 'Cards' },
            { value: 'table', icon: <Table2 aria-hidden />, 'aria-label': 'Table' },
          ]}
        />
      ) : null}

      {density ? (
        <SegmentedControl
          size="md"
          className="shrink-0"
          aria-label="Row density"
          value={density.value}
          disabled={density.disabled}
          onValueChange={(next) => density.onChange(next === 'compact' ? 'compact' : 'comfortable')}
          options={[
            { value: 'comfortable', icon: <Rows2 aria-hidden />, 'aria-label': 'Comfortable rows' },
            { value: 'compact', icon: <Rows3 aria-hidden />, 'aria-label': 'Compact rows' },
          ]}
        />
      ) : null}

      {hasRight ? (
        /* The cluster wraps too, and not only the row it sits on. On the board
           at 320px its four controls measure 310px inside a 296px content box:
           one `shrink-0` group wider than the line it wraps onto still spills,
           and `More actions` sat 2px past the edge. */
        <div className="ml-auto flex shrink-0 items-center gap-1.5 pl-2 mobile:flex-wrap mobile:justify-end mobile:gap-y-1.5 mobile:pl-0">
          {count ? <CountLabel {...count} /> : null}
          {secondary}
          {action}
          {overflow && overflow.length > 0 ? (
            <Menu
              items={overflow}
              align="end"
              label="More actions"
              trigger={
                <IconButton label="More actions" variant="ghost" className="shrink-0">
                  <MoreHorizontal aria-hidden />
                </IconButton>
              }
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
